import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, Typography, Grid, IconButton, Menu, Box, MenuItem } from "@mui/material";
import { useParams } from "react-router-dom";
import TableComponent from "components/grid";
import CustomPagination from "components/CustomPagination";
import { RxCross2 } from "react-icons/rx";
import { useGetAllProjectRiskQuery } from "store/slices/apis/projectRiskSlice";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StatusBadge from "components/badge/StatusBadge";
import RiskSeverityBadge from "components/badge/RiskSeverityBadge";
import moment from "moment";
import { LuEye } from "react-icons/lu";
import ViewRisk from "view/risk/ViewRisk";

const HeatMapRiskList = (props) => {
    const { open, handleClose, data } = props;
    const { projectId } = useParams();
    const [loading, setLoading] = useState(false);

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);

    const handleMenuOpen = (event, row) => {
        event.stopPropagation(); // prevent row click if any
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const handleMenuView = () => {
        if (menuRow) {
            handleViewClick(menuRow);
        }
        handleMenuClose();
    };

    const handleViewClick = (row) => {
        setIsView((prev) => !prev);
        setViewData(row);
    };

    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
    };

    const [sortOrder, setSortOrder] = React.useState('desc');
    const [sortBy, setSortBy] = React.useState('createdDate');

    // page size
    const [pageNumber, setPageNumber] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);

    const actionTrackerList = useGetAllProjectRiskQuery({ ...data, ProjectId: projectId, SortBy: sortBy, SortOrder: sortOrder, PageNumber: pageNumber, PageSize: pageSize })

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';

        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
    };

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
    };

    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, row)}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
         {
            name: "ID",
            field: "customId",
            width: "130px",
            sticky: true,
        },
        {
            name: "Title",
            field: "title",
            width: "200px",
            sticky: true,
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {/* <Avatar
                                sx={{
                                    width: 30,
                                    height: 30,
                                    bgcolor: getAvatarColor(row.category),
                                    color: "#fff",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                }}
                            >{row?.category?.[0] || "?"}</Avatar> */}
                    {row?.title}
                </Box>
            ),
        },
        {
            name: "Category",
            field: "category",
            sticky: true,
        },
        {
            name: 'Status',
            field: 'riskStatus',
            renderer: (row) => (
                <StatusBadge status={row?.riskStatus} />
            )
        },
        {
            name: "Likelihood",
            field: "likelihood",
        },
        {
            name: "Impact",
            field: "impact",
        },
        {
            name: "Risk Rating",
            field: "riskRating",
        },
        {
            name: "Severity",
            field: "severity",
            renderer: (row) => (
                <RiskSeverityBadge status={row?.severity} />
            )
        },
        {
            name: "Mitigation Action Status",
            field: "mitigationActionStatus",
        },
        {
            name: "Target Closure Date",
            field: "targetClosureDate",
            renderer: (row) => (
                row?.targetClosureDate ? moment(row.targetClosureDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Entity/Person Responsible",
            field: "entityPersonResponsible",
        },
    ];

    return (
        <Dialog open={open}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#ECECEC",
                pb: 2,
                pt: 3,
                width: "100%",
            }}>
                <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                    Risks
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => handleClose()}
                    sx={(theme) => ({
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    {/* <IoMdClose style={{ fontSize: "20px" }} /> */}
                    <RxCross2 style={{ fontSize: "30px", color: "#140F36" }} />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container>
                    <Grid size={{ md: 12 }}>
                        <TableComponent
                            columns={columns}
                            data={actionTrackerList?.data?.data}
                            createSortHandler={handleRequestSort}
                            loading={loading}
                            order={sortOrder}
                            orderBy={sortBy}
                        />
                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    mt: 1,
                                    borderRadius: "3px",
                                    minWidth: 140,
                                    border: "1px solid #E5E9EB",
                                    boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                    overflow: "hidden",
                                },
                            }}
                            MenuListProps={{
                                sx: {
                                    py: 0.5,
                                },
                            }}
                        >
                            <MenuItem
                                onClick={handleMenuView}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <LuEye size={16} style={{ color: "#4B5563" }} />
                                <span>View</span>
                            </MenuItem>

                            {/* <MenuItem
                                onClick={handleMenuEdit}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <EditIcon style={{ width: 14, height: 14 }} />
                                <span>Edit</span>
                            </MenuItem> */}
                        </Menu>
                    </Grid>
                    <Grid size={{ md: 12 }}>
                        <CustomPagination
                            totalCount={actionTrackerList?.data?.totalRecords || 0}
                            pageSize={pageSize}
                            page={pageNumber}
                            onPageChange={handlePageChange}
                            setCurrentPage={setPageNumber}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </Grid>
                    {isView && <ViewRisk open={isView} handleClose={handleViewClose} rowData={viewData} />}
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default HeatMapRiskList;
